// src/data/disciplineStageFromActs.js
//
// T5 / D-22 (2026-10-16): THE STAGE THE ACTS SUPPORT, SAID BESIDE THE LADDER. The 09-19 finding: "Advance to Resolved" is
// one click beside a timeline that knows better. The hearing acts (hearingEvents.js) and the appeal acts
// (disciplineAppealLadder.js) are dated records; this reads them and says how far up the ladder they carry the case, and
// whether the ladder's stage agrees.
//
// It does NOT gate the ladder. The ladder and who moves it are Bob's decisions of 2026-10-08 (Record 611, disciplineLadder.js
// header); a case can rightly pass Appeal Filed with no appeal act (nothing to appeal), and every case opened before the
// acts existed carries none. Refusing an Advance from here would strand those cases. What it does is make a disagreement
// visible to the officer at the moment he would click: the ladder ahead of the record, or the record ahead of the ladder.
//
// PURE. No imports beyond the ladder's own vocabulary.
import { LADDER, STAGE_LABELS } from './disciplineLadder.js';

// Each step the acts can prove, lowest first: the stage, and the acts (either one) that prove it.
export const PROOF = Object.freeze([
  { stage: 'investigation_ordered',  from: 'hearingEvents', kinds: ['hearing_noticed'] },
  { stage: 'investigation_complete', from: 'hearingEvents', kinds: ['hearing_held', 'waiver_accepted'] },
  { stage: 'appeal_filed',           from: 'appealActs',    kinds: ['appealed'] },
]);

const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
const earliest = (acts) => acts.slice().sort((a, b) => (String(a.on) < String(b.on) ? -1 : 1))[0];

// The highest stage the acts on record prove, with the act that proves it; null when the case carries no such act.
export function stageFromActs(kase) {
  const k = kase || {};
  let best = null;
  for (const p of PROOF) {
    const hits = list(k[p.from]).filter(a => p.kinds.indexOf(a.kind) !== -1);
    if (hits.length) {
      const a = earliest(hits);
      best = { stage: p.stage, label: STAGE_LABELS[p.stage], act: a.kind, actLabel: a.label || a.kind.replace(/_/g, ' '), on: a.on || null };
    }
  }
  return best;
}

// The ladder against the record. Returns null when there is nothing to say (no acts; a draft, GC review or resolved
// case - off the comparable part of the ladder), else { agree, ahead: 'ladder' | 'acts' | null, line }.
export function ladderAgainstActs(kase) {
  const k = kase || {};
  const st = String(k.status || '');
  if (['draft', 'gc_review', 'resolved'].indexOf(st) !== -1 || LADDER.indexOf(st) === -1) return null;
  const ev = stageFromActs(k);
  if (!ev) return null;
  const at = LADDER.indexOf(st), proved = LADDER.indexOf(ev.stage);
  const cite = ev.actLabel + (ev.on ? ' on ' + ev.on : '');
  if (at === proved) return { agree: true, ahead: null, line: 'The acts on record agree with the stage: ' + ev.label + ' (' + cite + ').' };
  if (at < proved) return { agree: false, ahead: 'acts', line: 'The acts on record show ' + ev.label + ' (' + cite + '); the case still reads ' + STAGE_LABELS[st] + '. Advance it to match the record.' };
  // The ladder is past what the acts prove. Past Appeal Filed only matters if an appeal act should exist, so the line
  // names the next act the record lacks rather than calling the stage wrong.
  const next = PROOF.find(p => LADDER.indexOf(p.stage) > proved && LADDER.indexOf(p.stage) <= at);
  return { agree: false, ahead: 'ladder', line: 'The case reads ' + STAGE_LABELS[st] + ', but the acts on record go only as far as ' + ev.label + ' (' + cite + ')'
    + (next ? '. No act on record shows ' + STAGE_LABELS[next.stage] + ' - record it if it happened.' : '.') };
}
